import { useState } from 'react';
import { toast } from 'sonner';
import { useExtendLicense } from '@/services/queries';
import { ApiError } from '@/services/api/client';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import type { CustomerLicense } from '@/types';

/**
 * Pushing a licence's term out by however many months the deal calls for -
 * a renewal, a goodwill extension, whatever the reason. Every still-live
 * seat on the licence moves, each from wherever its own expiry already sits,
 * which is why this asks for a licence rather than a single seat: the seats
 * on it move together, the same way they were bought together.
 */

/** A sanity bound, not a product rule - matches the server's own cap. */
const MAX_EXTEND_MONTHS = 120;

export function ExtendLicenseDialog({
  organizationId,
  licence,
  open,
  onOpenChange,
}: {
  organizationId: string;
  licence: CustomerLicense;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {/* Remounted per opening, so a half-typed number from last time is gone. */}
        <ExtendLicenseBody
          organizationId={organizationId}
          licence={licence}
          onClose={() => {
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function ExtendLicenseBody({
  organizationId,
  licence,
  onClose,
}: {
  organizationId: string;
  licence: CustomerLicense;
  onClose: () => void;
}) {
  const [monthsInput, setMonthsInput] = useState('12');
  const extend = useExtendLicense(organizationId);

  const months = Number(monthsInput);
  const valid = Number.isInteger(months) && months >= 1 && months <= MAX_EXTEND_MONTHS;

  const submit = () => {
    if (!valid) return;
    extend.mutate(
      { licenseId: licence.id, months },
      {
        onSuccess: (result) => {
          toast.success(
            `Extended by ${String(months)} month${months === 1 ? '' : 's'} on ` +
              `${String(result.seatsExtended)} seat${result.seatsExtended === 1 ? '' : 's'}.`,
          );
          onClose();
        },
        onError: (error) => {
          toast.error(error instanceof ApiError ? error.message : 'Could not extend the licence.');
        },
      },
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Extend this licence</DialogTitle>
        <DialogDescription>
          Every seat still in service on this licence moves forward by the same number of months,
          each from its own current expiry. Revoked and withdrawn seats are left alone.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-1.5">
        <Label htmlFor="extend-months">Months</Label>
        <Input
          id="extend-months"
          type="number"
          inputMode="numeric"
          min={1}
          max={MAX_EXTEND_MONTHS}
          autoFocus
          value={monthsInput}
          onChange={(event) => {
            setMonthsInput(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
        />
        {!valid && monthsInput !== '' && (
          <p className="text-xs text-destructive">Whole months, 1 to {MAX_EXTEND_MONTHS}.</p>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={extend.isPending}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!valid || extend.isPending}>
          {extend.isPending ? 'Extending…' : 'Extend licence'}
        </Button>
      </DialogFooter>
    </>
  );
}
